import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, File as FileIcon, Folder } from 'lucide-react';
import {
  getPublicFolder,
  publicFolderFileContentURL,
  type PubFolderView,
} from '@/api/publicDrive';

function fmtSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Public Drive folder viewer (131.34). Unauthenticated; navigable within the
 * shared folder's subtree. No AppShell chrome — standalone like the other /p and
 * public surfaces.
 */
export function PublicDriveFolderPage() {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const [folderId, setFolderId] = useState<string | undefined>(undefined);
  const [view, setView] = useState<PubFolderView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getPublicFolder(token, folderId)
      .then((v) => {
        if (!cancelled) setView(v);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, folderId]);

  return (
    <div className="min-h-screen bg-[var(--color-lop-bg-deep)] px-4 py-10 text-[var(--color-lop-text-primary)]">
      <div className="mx-auto max-w-2xl">
        {loading && <p className="text-sm text-[var(--color-lop-text-secondary)]">{t('publicDrive.loading')}</p>}
        {!loading && error && (
          <p className="rounded-lg border border-[var(--color-lop-border)] bg-[var(--color-lop-bg-card)] p-6 text-sm text-[var(--color-lop-danger)]">
            {t('publicDrive.invalid')}
          </p>
        )}
        {!loading && !error && view && (
          <>
            <div className="mb-4 flex items-center gap-2 text-sm text-[var(--color-lop-text-secondary)]">
              <button
                type="button"
                className="hover:text-[var(--color-lop-teal)] hover:underline"
                onClick={() => setFolderId(undefined)}
              >
                {view.root.name}
              </button>
              {view.current.id !== view.root.id && (
                <>
                  <span>/</span>
                  <span className="text-[var(--color-lop-text-primary)]">{view.current.name}</span>
                </>
              )}
            </div>
            <h1 className="mb-6 text-xl font-semibold">{view.current.name}</h1>

            <div className="overflow-hidden rounded-lg border border-[var(--color-lop-border)] bg-[var(--color-lop-bg-card)]">
              {view.folders.length === 0 && view.files.length === 0 ? (
                <p className="p-6 text-center text-sm text-[var(--color-lop-text-muted)]">
                  {t('publicDrive.empty')}
                </p>
              ) : (
                <ul className="divide-y divide-[var(--color-lop-border)]">
                  {view.folders.map((f) => (
                    <li key={f.id}>
                      <button
                        type="button"
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-[var(--color-lop-bg-card-hi)]"
                        onClick={() => setFolderId(f.id)}
                      >
                        <Folder className="h-5 w-5 shrink-0 text-[var(--color-lop-teal)]" />
                        <span className="truncate text-sm">{f.name}</span>
                      </button>
                    </li>
                  ))}
                  {view.files.map((f) => (
                    <li key={f.id} className="flex items-center gap-3 px-4 py-2.5">
                      <FileIcon className="h-5 w-5 shrink-0 text-[var(--color-lop-text-muted)]" />
                      <span className="min-w-0 flex-1 truncate text-sm">{f.name}</span>
                      <span className="text-xs text-[var(--color-lop-text-muted)]">{fmtSize(f.sizeBytes)}</span>
                      <a
                        href={publicFolderFileContentURL(token, f.id)}
                        className="flex items-center gap-1 text-xs text-[var(--color-lop-teal)] hover:underline"
                        download
                      >
                        <Download className="h-4 w-4" />
                        {t('publicDrive.download')}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
